/**
 * Running-session surface: pause banner with host recovery controls, the active instance(s), and the
 * board-preservation proof (root collapses to a summary strip while a child activity is on top).
 * docs/ARCHITECTURE.md §3 (pause policy), §4 (instance lifecycle), §12 (client shell).
 */
import { DECISION_MAKER_ID, HOT_POTATO_ID, MOMENTUM_WORLD_MODULE_IDS } from '@momentum/modules/manifests';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import '../immersive-board.css';
import { useReducedMotion } from '../shell/useReducedMotion.ts';
import { useSessionContext } from './SessionContext.ts';
import { InstanceHost } from './InstanceHost.tsx';
import { BoardTransitionGate } from './BoardTransitionGate.tsx';
import { DeadlineChip } from '../components/DeadlineChip.tsx';
import { strings } from '../shell/strings.ts';
import type { PauseReason } from '@momentum/shared';
import { DecisionNoteBridgeProvider } from './DecisionNoteBridge.tsx';
import { LiquidTransition } from './LiquidTransition.tsx';
import { ResetForNewGroup } from './ResetForNewGroup.tsx';

const PAUSE_REASON_TEXT: Record<PauseReason, string> = {
  host_paused: strings.play.reasonHostPaused,
  presenter_paused: strings.play.reasonPresenterPaused,
  participant_disconnected: strings.play.reasonParticipantDisconnected,
  server_restart: strings.play.reasonServerRestart,
  module_requested: strings.play.reasonModuleRequested,
};

export interface PlaySurfaceProps {
  /** Compact voice indicator strip for the running session (T6); kept small, so it lives in the header row. */
  voice?: ReactNode;
}

export function PlaySurface({ voice }: PlaySurfaceProps) {
  const { view, self, client, serverTimeOffset } = useSessionContext();
  const reducedMotion = useReducedMotion();
  const session = view.session;
  const focusInstance = view.child ?? view.root;
  // MOMENTUM_WORLD_MODULE_IDS gated it on `!view.child` before this task; the immersive board -> mini-game
  // transition (BoardTransitionGate, below) needs `immersive` to stay true once a child launches too, so it
  // can render its own blurred-board treatment instead of falling through to the generic non-immersive one.
  const decisionActive = focusInstance?.moduleId === DECISION_MAKER_ID;
  const surfaceRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!decisionActive) return;
    const surface = surfaceRef.current;
    const strip = surface?.querySelector('.momentum-voice-strip');
    if (!surface || !strip) return;
    const measure = () => surface.style.setProperty('--dm-voice-height', `${Math.ceil(strip.getBoundingClientRect().height) + 16}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    measure();
    return () => observer.disconnect();
  }, [decisionActive]);
  const activityActive = decisionActive || focusInstance?.moduleId === HOT_POTATO_ID;
  const immersive = !activityActive && MOMENTUM_WORLD_MODULE_IDS.includes(view.root?.moduleId ?? '');
  // While the immersive transition (or the mini-game it hands off to) owns the screen, its own HUD
  // (BoardTransition's title/crystal-row/map-button pill) supersedes this plain heading rather than doubling
  // up with it — see BoardTransitionGate.tsx and docs/concept/board/06-minimal-transition.png.
  const showPlainHeading = !activityActive && !(immersive && view.child);

  const surface = (
    <div ref={surfaceRef} className={decisionActive ? "stack decision-play" : activityActive ? "stack hot-potato-play" : immersive ? "stack momentum-play" : "stack"} style={immersive || activityActive ? undefined : { paddingBottom: 110 }}>
      {showPlainHeading ? (
        <div className={immersive ? "row-between momentum-play-heading" : "row-between"}>
          <h1>{immersive ? "Momentum" : strings.play.title}</h1>
          <span className="row" aria-live="polite">
            {!immersive && focusInstance?.deadlines.map((deadline) => (
              <DeadlineChip key={deadline.id} deadline={deadline} serverTimeOffset={serverTimeOffset} />
            ))}
          </span>
        </div>
      ) : null}

      {voice ? <div className="play-surface__persistent-voice" style={{ maxWidth: 420 }}>{voice}</div> : null}

      {session.pause ? (
        <div className="banner banner-pause stack-sm" role="status" aria-live="polite">
          <strong>{strings.play.pauseTitle}</strong>
          <p>{PAUSE_REASON_TEXT[session.pause.reason]}</p>
          {session.pause.detail ? <p className="text-muted">{session.pause.detail}</p> : null}
          {session.pause.waitingFor.length > 0 ? (
            <p>
              {strings.play.pauseWaitingFor}:{' '}
              {session.pause.waitingFor
                .map((id) => session.participants.find((p) => p.id === id)?.displayName ?? id)
                .join(', ')}
            </p>
          ) : null}
          {self && self.isHost ? (
            <div>
              <button type="button" className="button button-primary" onClick={() => client.send('session.resume', {}, 'session')}>
                {strings.play.resume}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {self && self.isHost ? (immersive || activityActive) ? (
        <details className="momentum-host-tools"><summary>Session controls</summary><HostControls /></details>
      ) : <HostControls /> : null}

      {activityActive && focusInstance ? (
        <InstanceHost instance={focusInstance} session={session} you={view.you} client={client}
          serverTimeOffset={serverTimeOffset} reducedMotion={reducedMotion} asParticipantId={view.viewingAs ?? undefined} />
      ) : view.root && immersive && view.child && session.child ? (
        // Immersive world, child launched: the board -> mini-game transition covers the gap between them
        // (docs/concept/board/06-minimal-transition.png) instead of the generic "board preserved" strip below.
        // Unreachable today — momentum-board-v2 sets `launchesChildren: false` — but wired correctly for when
        // a real mini-game module exists; see BoardTransitionGate.tsx's header for the honest limitations.
        <div className="momentum-world">
          <BoardTransitionGate
            child={session.child}
            childModuleName={view.child.moduleName}
            participants={session.participants}
            self={view.you}
            serverTimeOffset={serverTimeOffset}
            reducedMotion={reducedMotion}
            rootView={view.root.view}
            rootBackdrop={
              <InstanceHost
                instance={view.root}
                session={session}
                you={view.you}
                client={client}
                serverTimeOffset={serverTimeOffset}
                reducedMotion={reducedMotion}
                asParticipantId={view.viewingAs ?? undefined}
              />
            }
          >
            <InstanceHost
              instance={view.child}
              session={session}
              you={view.you}
              client={client}
              serverTimeOffset={serverTimeOffset}
              reducedMotion={reducedMotion}
              asParticipantId={view.viewingAs ?? undefined}
            />
          </BoardTransitionGate>
        </div>
      ) : (
        <>
          {view.root ? (
            <div className={immersive ? "momentum-world" : undefined} style={view.child ? { opacity: 0.75 } : undefined}>
              {view.child ? (
                <p className="badge badge-accent" role="status">
                  {strings.play.boardPreserved} — {strings.play.boardPreservedHint}
                </p>
              ) : null}
              <div style={view.child ? { maxHeight: 220, overflow: 'hidden', pointerEvents: 'none' } : undefined}>
                <InstanceHost
                  instance={view.root}
                  session={session}
                  you={view.you}
                  client={client}
                  serverTimeOffset={serverTimeOffset}
                  reducedMotion={reducedMotion}
                  asParticipantId={view.viewingAs ?? undefined}
                />
              </div>
            </div>
          ) : null}

          {view.child ? (
            <InstanceHost
              instance={view.child}
              session={session}
              you={view.you}
              client={client}
              serverTimeOffset={serverTimeOffset}
              reducedMotion={reducedMotion}
              asParticipantId={view.viewingAs ?? undefined}
            />
          ) : null}
        </>
      )}
    </div>
  );

  // Keying the bridge to the focused instance guarantees that a returning board, a new
  // child, or a presenter projection switch cannot inherit an old note callback.
  const bridgedSurface = decisionActive && voice
    ? <DecisionNoteBridgeProvider key={focusInstance?.instanceId}>{surface}</DecisionNoteBridgeProvider>
    : surface;

  // This wrapper stays mounted while the renderer and Decision Maker bridge swap beneath it. That makes
  // the authoritative child instance identity — rather than renderer-local readiness — the sole trigger.
  return (
    <LiquidTransition
      childInstanceId={view.child?.instanceId ?? null}
      childTitle={view.child?.moduleName ?? null}
      reducedMotion={reducedMotion}
    >
      {bridgedSurface}
    </LiquidTransition>
  );
}

function HostControls() {
  const { view, client } = useSessionContext();
  const session = view.session;

  return (
    <section className="card row" aria-label={strings.play.hostControls}>
      <select
        aria-label={strings.play.removeParticipant}
        defaultValue=""
        onChange={(e) => {
          const participantId = e.target.value;
          if (!participantId) return;
          client.send('session.remove_participant', { participantId }, 'session');
          e.target.value = '';
        }}
      >
        <option value="">{strings.play.removeParticipant}</option>
        {session.participants.map((p) => (
          <option key={p.id} value={p.id}>
            {p.displayName}
          </option>
        ))}
      </select>
      {session.child ? (
        <button type="button" className="button" onClick={() => client.send('session.abandon_child', {}, 'session')}>
          {strings.play.abandonActivity}
        </button>
      ) : null}
      <button type="button" className="button button-danger" onClick={() => client.send('session.end', {}, 'session')}>
        {strings.play.endSession}
      </button>
      <ResetForNewGroup />
    </section>
  );
}
